import React from 'react';
import MaterialTable from '../../Components/DataTable/DataTable';
import apiClient, { getAccessToken } from '../../apiClient';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import DateFnsUtils from '@date-io/date-fns';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import './Reports.scss';
import Button from '@material-ui/core/Button';
import moment from 'moment';
import Spinner from '../Spinner';
import ReactDOM from 'react-dom';
import { saveAs } from 'file-saver';
import Multiselect from 'multiselect-react-dropdown';
import withFlashNotification from '../Notification/withFlashNotification';
import { notifyApiError } from '../Notification/notificationHelpers';
import { createCsvContent, createXlsxBuffer } from '../../spreadsheet';

function eodStyledCell(value, rowColor, bold) {
  const style = { fill: { fgColor: { rgb: rowColor } } };
  if (bold) {
    style.font = { bold: true };
  }
  return { v: value, s: style };
}

function roundReportNumber(value) {
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Treat values that round to 0 at 4 decimal places as zero (e.g. floating-point noise). */
function roundQtyTo4(value) {
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.round((n + Number.EPSILON) * 10000) / 10000;
}

function hasNonZeroInventoryQty(value) {
  return Math.abs(roundQtyTo4(value)) > 0;
}

function formatReportQty(value) {
  return roundReportNumber(value).toFixed(2);
}

function formatReportMoney(value) {
  return `$${formatReportQty(value)}`;
}

function eodRoundTo2(qty) {
  return roundReportNumber(qty);
}

function reportQtyCell(value, rowColor, bold = false) {
  const rounded = roundReportNumber(value);
  const style = {
    fill: { fgColor: { rgb: rowColor || 'FFFFFF' } },
    numFmt: '0.00',
  };
  if (bold) {
    style.font = { bold: true };
  }
  return { v: rounded, t: 'n', s: style };
}

function reportMoneyCell(value, rowColor, bold = false) {
  const rounded = roundReportNumber(value);
  const style = {
    fill: { fgColor: { rgb: rowColor || 'FFFFFF' } },
    numFmt: '$#,##0.00',
  };
  if (bold) {
    style.font = { bold: true };
  }
  return { v: rounded, t: 'n', s: style };
}

function eodQtyCell(qty, rowColor) {
  const rounded = eodRoundTo2(qty);
  if (rounded === 0) {
    return eodStyledCell('-', rowColor);
  }
  return {
    v: rounded,
    t: 'n',
    s: {
      fill: { fgColor: { rgb: rowColor } },
      numFmt: '0.00',
    },
  };
}

const SINGLE_DATE_REPORTS = [
  'InventoryTurnoverReport',
  'InventoryAgeReport',
  'EODReport',
  'PSReport',
];

const TURNOVER_TRANSACTION_REPORTS = ['InventoryTurnoverReport', 'PSReport'];
const CHK_TRANSACTION_REPORTS = ['InventoryAgeReport'];
// Reports whose date range is optional (pickers shown, not required).
const OPTIONAL_DATE_REPORTS = ['SPLReport'];

function getInventoryAgeRowColor(diffInDays) {
  const days = Number(diffInDays) || 0;
  if (days >= 15 && days <= 21) return 'FFFF00';
  if (days >= 22 && days <= 35) return 'FFA500';
  if (days >= 36) return 'FF0000';
  return 'FFFFFF';
}

function isAgedInventoryUnit(diffInDays) {
  return (Number(diffInDays) || 0) >= 15;
}

function parseReportMoment(raw) {
  if (raw == null || raw === '') return moment.invalid();
  if (moment.isMoment(raw)) return raw.clone();
  if (raw instanceof Date) return moment(raw);
  const text = String(raw).trim();
  const strict = moment(text, ['MM-DD-YYYY', 'M-D-YYYY', 'YYYY-MM-DD'], true);
  if (strict.isValid()) return strict;
  const loose = moment(text);
  return loose.isValid() ? loose : moment.invalid();
}

function formatReportApiDate(raw) {
  const m = parseReportMoment(raw);
  return m.isValid() ? m.format('MM-DD-YYYY') : '';
}

function isTxnOnOrBeforeReportDate(txnDate, reportEndDate) {
  const asOf = parseReportMoment(reportEndDate);
  const txn = parseReportMoment(txnDate);
  return asOf.isValid() && txn.isValid() && !txn.isAfter(asOf, 'day');
}

function getEarliestChkTxnForItem(transactions, itemId, reportEndDate) {
  const id = Number(itemId);
  if (Number.isNaN(id)) return null;
  const rows = (transactions || [])
    .filter(
      (row) =>
        Number(row.ItemId) === id &&
        String(row.TicketNumber || '').includes('CHK') &&
        isTxnOnOrBeforeReportDate(row.Date, reportEndDate)
    )
    .sort(
      (a, b) => parseReportMoment(a.Date).valueOf() - parseReportMoment(b.Date).valueOf()
    );
  return rows.length > 0 ? rows[0] : null;
}

function getOutQuantityForItem(transactions, itemId, reportEndDate, toQty) {
  const id = Number(itemId);
  if (Number.isNaN(id)) return 0;
  return (transactions || [])
    .filter(
      (row) =>
        Number(row.ItemId) === id &&
        String(row.TicketNumber || '').includes('OUT') &&
        isTxnOnOrBeforeReportDate(row.Date, reportEndDate)
    )
    .reduce((sum, row) => sum + toQty(row.Quantity), 0);
}

function buildTurnOverStyleRow(item, itemDetail, asOfDate, soldQty) {
  const reportDate = parseReportMoment(asOfDate);
  const date1 = parseReportMoment(itemDetail.Date);
  const date2 = reportDate.isValid() ? reportDate : moment();
  const diffInDays = date2.diff(date1, 'days');
  const daysAged = Number.isFinite(diffInDays) && diffInDays > 0 ? diffInDays : 0;
  const turnOverRatio = daysAged > 0 ? soldQty / daysAged : 0;
  const safeTurnOverRatio = Number.isFinite(turnOverRatio)
    ? Number(turnOverRatio.toFixed(2))
    : 0;
  const qty = roundQtyTo4(item && item.Quantity);

  return {
    daysAged,
    purchaseDate: itemDetail.Date,
    itemNumber: itemDetail.ItemNumber || item.ItemNumber,
    vendorName: item.VendorName,
    itemName: item.ItemName,
    quantity: qty,
    cost: itemDetail.PricePerUnit,
    price: itemDetail.TargetPrice == null ? 0 : itemDetail.TargetPrice,
    turnOverRatio: safeTurnOverRatio,
  };
}

function inventoryAgeStyledCell(value, diffInDays) {
  const rowColor = getInventoryAgeRowColor(diffInDays);
  return { v: value, s: { fill: { fgColor: { rgb: rowColor } } } };
}

function inventoryAgeQtyCell(value, diffInDays) {
  return reportQtyCell(value, getInventoryAgeRowColor(diffInDays));
}

function inventoryAgeMoneyCell(value, diffInDays) {
  return reportMoneyCell(value, getInventoryAgeRowColor(diffInDays));
}

function inventoryAgeBoldCell(value, rowColor) {
  const n = Number.parseFloat(value);
  if (Number.isFinite(n)) {
    return reportQtyCell(n, rowColor || 'E8E8E8', true);
  }
  return {
    v: value,
    s: {
      fill: { fgColor: { rgb: rowColor || 'FFFFFF' } },
      font: { bold: true },
    },
  };
}

class Reports extends React.Component {
  state = {
    splHideInactive: true,
        Results: [],
        ItemsPurchaseInfo:[],
        options : ['React', 'Vue', 'Angular'],
        categoryList : [],
        data: [
          ['Date', 'Ticket', 'Type', 'Item Number', 'Item Name', 'Qty', 'Price', 'Purchase', 'Sale'],
          
        ],
        headersTicket: [
          { label: "Ticket Number", key: "TicketNumber" },
          { label: "PO Number", key: "PONumber" },
          { label: "Date", key: "Date" },
          { label: "User Name", key: "UserName" },
          { label: "Vendor Name", key: "VendorName" },
          { label: "Customer Name", key: "CustomerName" },
          { label: "Total Ticket Price", key: "TotalTicketPrice" },
        ],
        ReportData: [],
        loading:false,
        TicketDetail:[],
        submitted: false,
      showTranDetail:false,
      showTranEdit:false,
      errorMessage:'',
      action:'edit',
      tranDetail:[],
      fullWidth:true,
      maxWidth:'md',
      maxEditWidth:'lg',
      search:{startDate:null,endDate:null,vendorId:0,customerId:0,ponumber:"",ticketnumber:"",tabindex:0,report:''},
      apiVendorData:[],
      apiCustomerData:[],
      fullGroupData:[],
      inventoryData:[],
      hasContent:false,
      index:0,
      startDate:null,
      endDate:null,
      selectedList:[]
       
    }

    toQty = (value) => {
      const n = Number.parseFloat(value);
      return Number.isFinite(n) ? n : 0;
    };

    formatReportCellValue = (value) => {
      if (value === '' || value === null || value === undefined || value === '-') {
        return value === undefined || value === null ? '' : value;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.startsWith('$')) {
          const moneyRaw = trimmed.replace(/[$,]/g, '');
          // Require a fully numeric amount (avoid parseFloat truncating "12 text")
          if (/^-?\d+(\.\d+)?$/.test(moneyRaw)) {
            return formatReportMoney(Number.parseFloat(moneyRaw));
          }
          return trimmed;
        }
        // Only format pure numeric strings — parseFloat("12345 Item Name") would drop the name
        if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
          return formatReportQty(Number.parseFloat(trimmed));
        }
        return trimmed;
      }
      if (typeof value === 'number') {
        return formatReportQty(value);
      }
      return value;
    };

    formatItemNumberAndName = (itemNumber, itemName) => {
      const num = String(itemNumber ?? '').trim();
      const name = String(itemName ?? '').trim();
      if (num && name) return `${num} ${name}`;
      return num || name;
    };

    handleChange = (e) => {
    const values = Array.from(e.target.selectedOptions, (option) => option.value);
    this.setState({selected : values});
  };

  handleApiFailure = (error, fallbackMessage) => {
    const message = notifyApiError(
      this.props.showFlashNotification,
      error,
      fallbackMessage
    );
    this.setState({ errorMessage: message, loading: false });
    setTimeout(() => this.setState({ errorMessage: null }), 6000);
  };
    
    handleLinkClick = (items) => {
      this.setState({ showTranDetail: true });
      this.setState({tranDetail: items});
    };

    handleLinkEditClick = (items,action) => {
      
      this.setState({ action: action });
      this.setState({ showTranEdit: true });
      this.setState({tranDetail: items});
    };
generateOrderReport = (reporName) => {
  let totalPO = 0;
  let totalInvoice = 0;
  this.state.data.push(['Date', 'Ticket Number', 'Type', 'Item Number', 'Item Name', 'Item Category', 'Quantity', 'Price Per Unit', 'Total Price Purchase', 'Total Price Order' ]);
  this.state.Results.forEach((item) => {
    let type = this.getTransactionType(item.TicketNumber);
    if(type=='PO')
    {
      totalPO = totalPO + item.TotalPrice;
    }else{
      totalInvoice = totalInvoice + item.TotalPrice;
    }
    this.state.data.push([
      item.Date,
      item.TicketNumber,
      type,
      item.ItemNumber,
      item.ItemName,
      item.CategoryName || '',
      formatReportQty(item.Quantity),
      formatReportMoney(item.PricePerUnit),
      type == 'PO' ? formatReportMoney(item.TotalPrice) : '',
      type == 'Invoice' ? formatReportMoney(item.TotalPrice) : '',
    ]);
  });

  this.state.data.push(['', '', '', '', '', '', '', 'Total', formatReportMoney(totalPO), formatReportMoney(totalInvoice)]);

  const excelBuffer = createCsvContent(this.state.data);
  const currentDateTime = new Date();

  // Format date-time stamp
  const formattedDateTime = `${currentDateTime.toLocaleDateString()} ${currentDateTime.toLocaleTimeString()}`;

  this.saveExcelFile(excelBuffer, reporName + '_report_'+formattedDateTime+'.csv');

 
   
 

  const { history } = this.props;
history.push('/vendor-details');
  setTimeout(() => {
    history.push('/reports');
   
  }, 1); 

 
};


groupBy(list, field) {
  const groups = {};
  for (const item of list) {
    const key = item[field];
    groups[key] = groups[key] || [];
    groups[key].push(item);
  }
  return Object.values(groups);
}



addColumn(columnName) {
  this.state.inventoryData.forEach(item => {
      if (!(columnName in item)) {
          Object.defineProperty(item, columnName, {
              value: undefined,
              writable: true,
              enumerable: true,
              configurable: true
          });
      }
  });
}
generateInventoryArray(){
  let accessToken = getAccessToken();
  var that = this;
  const params = {
    date:  this.state.startDate ,
     categoryIds: this.state.selectedList.map(e => e.CategoryId).join(","),
    includeInactive: 1,
    onlyWithTransactions: 1,
    txnStartDate: this.state.startDate
      ? moment(this.state.startDate).format('MM-DD-YYYY')
      : '',
    txnEndDate: this.state.endDate
      ? moment(this.state.endDate).format('MM-DD-YYYY')
      : '',
    };

    
  apiClient.get(`/api/get/itemByDate`, { params: params ,headers: {"Authorization" : `Bearer ${accessToken}`} })
  .then(res => {
    const Results = res.data;
    if(Results.length == 0)
      {

        var errorMessage = 'No record available for inventory for from date, Please select another date.';
        setTimeout(() => {
          this.setState({ errorMessage:errorMessage });
  
        }, 1000);
         
        setTimeout(() => {
          this.setState({ errorMessage: null });
         }, 6000);

         this.setState({search:{startDate:null,endDate:null,vendorId:0,customerId:0,ponumber:"",ticketnumber:"",tabindex:0,report:'',}});
        this.setState({selectedList:[]});

         return;

      }else{

        this.setState({search:{startDate:null,endDate:null,vendorId:0,customerId:0,ponumber:"",ticketnumber:"",tabindex:0,report:'',}});
        this.setState({selectedList:[]});
        Results.forEach(function(item){
          var normalizedActive = String(item.Active == null ? 1 : item.Active).toLowerCase();
          var isActiveItem = normalizedActive === '1' || normalizedActive === 'true';
          var hasTxnInRange = (that.state.Results || []).some(
            (txn) => Number(txn.ItemId) === Number(item.ItemId)
          );
          if (!isActiveItem && !hasTxnInRange) {
            return;
          }
          var purchaseInfo = that.state.ItemsPurchaseInfo.filter(x=>x.ItemId == item.ItemId);
          var purchaseDate = '';
          var cost =0;
          if(purchaseInfo.length > 0){
            
            purchaseDate = purchaseInfo[0]?.Date;
            cost = formatReportMoney(purchaseInfo[0]?.TotalPrice);
          }
          that.state.inventoryData.push({
            _itemNumber: item['ItemNumber'],
            'Item Number + Item Name': that.formatItemNumberAndName(item['ItemNumber'], item['ItemName']),
            'Category': item['CategoryName'],
            'MetrcUID': (item['MetricUID'] || item['MetrcUID'] || item['MatrcUID'] || item['MatricUID'] || ''),
            'Vendor Name': item['VendorName'],
            'Purchase Date': purchaseDate,
            'Cost': cost,
            // Numeric so Ending Inventory sums use addition (not string concat on "0.00")
            'No of Unit': that.toQty(item['Quantity'])
          })
        });
    
       
     
    
      ///   making reserve section
    
      var reserveRecords = this.state.Results.filter(x=>x.TransactionType == 'Reserve');
      
      this.groupBy(reserveRecords, 'CustomerName').forEach(function(groupReserveCustomer){
           that.addColumn(groupReserveCustomer[0]['CustomerName']+'#Reserve');
      });
    
      reserveRecords.forEach(function(reserveItem){
    
       var records = that.state.inventoryData.filter(x=>x._itemNumber == reserveItem.ItemNumber);
       if(records.length > 0)
       {
        var returnValue = 0;
                  if(records[0][reserveItem['CustomerName'] +'#Reserve'] != undefined)
                    returnValue = parseFloat(records[0][reserveItem['CustomerName'] +'#Reserve']);

        records[0][reserveItem['CustomerName'] +'#Reserve'] = that.toQty(reserveItem['Quantity']) + returnValue;
       }
    
      });
    
      ////  group date wise data
    
      var transactionsData = this.state.Results.filter(x=>x.TransactionType != 'Reserve');
    
      this.groupBy(transactionsData, 'Date').forEach(function(dateGroups){
        that.groupBy(dateGroups, 'TransactionType').forEach(function(item){
          
          if(item[0]['TransactionType']=='CheckOut')
          {
            that.groupBy(item, 'CustomerName').forEach(function(itemOrder){
              that.addColumn(itemOrder[0]['CustomerName']+ '#'+ itemOrder[0]['Date'] +'#CheckOut');
            });
          }
          else if(item[0]['TransactionType']=='Return')
          {
            that.groupBy(item, 'CustomerName').forEach(function(itemReturn){
              if(itemReturn[0]['CustomerName'] != null)
              that.addColumn(itemReturn[0]['CustomerName']+ '#'+ itemReturn[0]['Date'] +'#Return');
            });
            that.groupBy(item, 'VendorName').forEach(function(itemReturn){
              if(itemReturn[0]['VendorName'] != null)
              that.addColumn(itemReturn[0]['VendorName']+ '#'+ itemReturn[0]['Date'] +'#Return');
            });
          }
          else if(item[0]['TransactionType']=='Adjustment')
          {
            that.groupBy(item, 'CustomerName').forEach(function(itemAdj){
              that.addColumn(itemAdj[0]['CustomerName']+ '#'+ itemAdj[0]['Date'] +'#Adjustment');
            });
          }
          else if(item[0]['TransactionType']=='Order')
          {
           
            that.groupBy(item, 'VendorName').forEach(function(itemInvoice){
              that.addColumn(itemInvoice[0]['VendorName']+ '#'+ itemInvoice[0]['Date'] +'#Order');
            });
          }
    
         });
        });
    
        ///  adding records in data
    
        this.groupBy(transactionsData, 'Date').forEach(function(dataGroups){
          
    
                var checkOutRecords = dataGroups.filter(x=>x.TransactionType == 'CheckOut' );
              
                checkOutRecords.forEach(function(checkOutItem){
              
                  var checkOutRecord = that.state.inventoryData.filter(x=>x._itemNumber == checkOutItem.ItemNumber);
                  if(checkOutRecord.length > 0)
                  {
                    var checkOutValue = 0;
                  if(checkOutRecord[0][checkOutItem['CustomerName']+ '#' + dataGroups[0]['Date'] +'#CheckOut'] != undefined)
                    checkOutValue = parseFloat(checkOutRecord[0][checkOutItem['CustomerName']+ '#' + dataGroups[0]['Date'] +'#CheckOut']);

                    checkOutRecord[0][checkOutItem['CustomerName']+ '#' + dataGroups[0]['Date'] +'#CheckOut'] =  checkOutItem['Quantity'] != undefined? ((-1 * that.toQty(checkOutItem['Quantity'])) + checkOutValue):'';
                  }
              
                });
                var returnRecords = dataGroups.filter(x=>x.TransactionType == 'Return' );
              
                returnRecords.forEach(function(returnItem){
              
                var returnRecord = that.state.inventoryData.filter(x=>x._itemNumber == returnItem.ItemNumber);
                if(returnRecord.length > 0)
                {
                    var returnValue = 0;
                  if(returnRecord[0][returnItem['VendorName']+ '#' + dataGroups[0]['Date'] +'#Return'] != undefined)
                    returnValue = parseFloat(returnRecord[0][returnItem['VendorName']+ '#' + dataGroups[0]['Date'] +'#Return']);



                  if(returnItem['CustomerName'] == undefined)
                  {
                    returnRecord[0][returnItem['VendorName']+ '#' + dataGroups[0]['Date'] +'#Return'] =returnItem['Quantity'] != undefined? ((-1 * that.toQty(returnItem['Quantity'])) + returnValue):''; 
                  }else{
                    returnRecord[0][returnItem['CustomerName']+ '#' + dataGroups[0]['Date'] +'#Return'] =returnItem['Quantity'] != undefined? ( that.toQty(returnItem['Quantity']) + returnValue):''; 
                  }
                 
                }
              
                });
    
                var adjustmentRecords = dataGroups.filter(x=>x.TransactionType == 'Adjustment' );
              
                adjustmentRecords.forEach(function(adjustmentItem){
              
                var adjustmentRecord = that.state.inventoryData.filter(x=>x._itemNumber == adjustmentItem.ItemNumber);
                if(adjustmentRecord.length > 0)
                {

                  var adjustmentValue = 0;
                  if(adjustmentRecord[0][adjustmentItem['CustomerName']+ '#' + dataGroups[0]['Date'] +'#Adjustment'] != undefined)
                    adjustmentValue = parseFloat(adjustmentRecord[0][adjustmentItem['CustomerName']+ '#' + dataGroups[0]['Date'] +'#Adjustment']);


                  adjustmentRecord[0][adjustmentItem['CustomerName']+ '#' + dataGroups[0]['Date'] +'#Adjustment'] =  adjustmentItem['Quantity'] != undefined? ( that.toQty(adjustmentItem['Quantity']) + adjustmentValue):'';
                }
              
                });
    
                var orderRecords = dataGroups.filter(x=>x.TransactionType == 'Order' );
              
                orderRecords.forEach(function(orderItem){
              
                var orderRecord = that.state.inventoryData.filter(x=>x._itemNumber == orderItem.ItemNumber);
                if(orderRecord.length > 0)
                {
                   var orderValue = 0;
                  if(orderRecord[0][orderItem['VendorName']+ '#' + dataGroups[0]['Date'] +'#Order'] != undefined)
                    orderValue = parseFloat(orderRecord[0][orderItem['VendorName']+ '#' + dataGroups[0]['Date'] +'#Order']);


                  orderRecord[0][orderItem['VendorName']+ '#' + dataGroups[0]['Date'] +'#Order'] = that.toQty(orderItem['Quantity']) + orderValue;
                }
              
                });
    
        });
    
        that.addColumn('Ending Inventory');
        that.addColumn('Reserves');
        that.addColumn('Ending Inventory with Reserves');
    
    
      let entries = Object.entries(that.state.inventoryData[0]).filter(([key]) => key !== '_itemNumber');
      const noOfUnitIndex = entries.findIndex(([key]) => key === 'No of Unit');
      const qtyStartIndex = noOfUnitIndex >= 0 ? noOfUnitIndex : 6; // 0-based; fallback keeps prior behavior
      const summaryKeys = {
        endingInventory: 'Ending Inventory',
        reserves: 'Reserves',
        endingWithReserves: 'Ending Inventory with Reserves',
      };
    
      let row1Key = [];
      let row2Key = [];
      let rowFooter = [];
      entries.forEach(([key], index) => {
        let arr = key.split('#');
        if(arr.length > 1)
        {
          row1Key.push(arr[1]);
          row2Key.push(arr[0]);
        }
        else{
          row1Key.push(arr[0]);
          row2Key.push('');
        }

        if (index === 0) {
          rowFooter.push('Totals');
        } else if (
          index >= qtyStartIndex ||
          key === summaryKeys.endingInventory ||
          key === summaryKeys.reserves ||
          key === summaryKeys.endingWithReserves ||
          key === 'Cost'
        ) {
          rowFooter.push(0);
        } else {
          rowFooter.push('');
        }
      });
    
    this.state.data.push(row1Key);
    this.state.data.push(row2Key);
    that.state.inventoryData.forEach((item) => {
      let rowValue = [];
      let reserveTotal = 0;
      let otherTotal = 0;
      entries.forEach(([key], index) => {
        const isSummaryColumn =
          key === summaryKeys.endingInventory ||
          key === summaryKeys.reserves ||
          key === summaryKeys.endingWithReserves;

        if (!isSummaryColumn && index >= qtyStartIndex) {
          const cellQty = roundReportNumber(that.toQty(item[key]));
          if (key.indexOf('Reserve') !== -1) {
            reserveTotal += cellQty;
            otherTotal -= cellQty;
          } else {
            otherTotal += cellQty;
          }
          rowFooter[index] = roundReportNumber(rowFooter[index] + cellQty);
          rowValue.push(that.formatReportCellValue(item[key]));
          return;
        }

        if (key === summaryKeys.endingInventory) {
          const endingWithReserves = otherTotal + reserveTotal;
          const showDash = roundReportNumber(endingWithReserves) === 0;
          const endingInvValue = showDash ? 0 : roundReportNumber(otherTotal);
          rowFooter[index] = roundReportNumber(rowFooter[index] + endingInvValue);
          rowValue.push(showDash ? '-' : formatReportQty(otherTotal));
          return;
        }

        if (key === summaryKeys.reserves) {
          const showDash = roundReportNumber(reserveTotal) === 0;
          const reservesValue = showDash ? 0 : roundReportNumber(reserveTotal);
          rowFooter[index] = roundReportNumber(rowFooter[index] + reservesValue);
          rowValue.push(showDash ? '-' : formatReportQty(reserveTotal));
          return;
        }

        if (key === summaryKeys.endingWithReserves) {
          const endingWithReserves = otherTotal + reserveTotal;
          const showDash = roundReportNumber(endingWithReserves) === 0;
          const endingWithReservesValue = showDash ? 0 : roundReportNumber(endingWithReserves);
          rowFooter[index] = roundReportNumber(rowFooter[index] + endingWithReservesValue);
          rowValue.push(showDash ? '-' : formatReportQty(endingWithReserves));
          return;
        }

        if (key === 'Cost') {
          const costQty = that.toQty(
            typeof item[key] === 'string' ? String(item[key]).replace(/[$,]/g, '') : item[key]
          );
          rowFooter[index] = roundReportNumber(rowFooter[index] + costQty);
        }

        rowValue.push(that.formatReportCellValue(item[key]));
      });
      
      
      this.state.data.push(rowValue);
    });
    this.state.data.push(
      rowFooter.map((val, idx) => {
        if (idx === 0) return val;
        if (val === '' || val === null || val === undefined) return '';
        const colKey = entries[idx] && entries[idx][0];
        if (colKey === 'Cost') {
          return formatReportMoney(val);
        }
        return that.formatReportCellValue(val);
      })
    );
    
    this.setState({search:{startDate:null,endDate:null,vendorId:0,customerId:0,ponumber:"",ticketnumber:"",tabindex:0,report:''}});
    this.setState({selectedList:[]});
    const excelBuffer = createCsvContent(this.state.data);
    const currentDateTime = new Date();
    
    // Format date-time stamp
    const formattedDateTime = `${currentDateTime.toLocaleDateString()} ${currentDateTime.toLocaleTimeString()}`;
    
    this.saveExcelFile(excelBuffer, "Inventory" + '_report_'+formattedDateTime+'.csv');
    this.setState({search:{startDate:null,endDate:null,vendorId:0,customerId:0,ponumber:"",ticketnumber:"",tabindex:0,report:''}});
    this.setState({selectedList:[]});
    const { history } = this.props;
    history.push('/vendor-details');
      setTimeout(() => {
        history.push('/reports');
       
      }, 1); 
    

      }

  })
  .catch(error => this.handleApiFailure(error, 'Unable to generate the inventory report.'));
   
  
}

generateInventoryTurnOverArray(){
 let accessToken = getAccessToken();
  const asOfDate =
    this.state.search.endDate != null && this.state.search.endDate !== ''
      ? this.state.search.endDate
      : this.state.endDate;
  const params = {
    date: formatReportApiDate(asOfDate),
      categoryIds: this.state.selectedList.map(e => e.CategoryId).join(","),
    };

    
  apiClient.get(`/api/get/itemByDate`, { params: params ,headers: {"Authorization" : `Bearer ${accessToken}`} })
  .then(res => {
    const NewResults = res.data;
    if(NewResults.length == 0)
      {

        var errorMessage = 'No record available for inventory for from date, Please select another date.';
        setTimeout(() => {
          this.setState({ errorMessage:errorMessage });
  
        }, 1000);
         
        setTimeout(() => {
          this.setState({ errorMessage: null });
         }, 6000);

         this.setState({search:{startDate:null,endDate:null,vendorId:0,customerId:0,ponumber:"",ticketnumber:"",tabindex:0,report:'',}});
        this.setState({selectedList:[]});

         return;

      }else{

        this.setState({search:{startDate:null,endDate:null,vendorId:0,customerId:0,ponumber:"",ticketnumber:"",tabindex:0,report:''}});
        this.setState({selectedList:[]});
       if(NewResults.length > 0)
       {
        var that = this;
var  NewResults1 = NewResults.filter(x=>that.toQty(x.Quantity) > 0);

       this.state.data.push(['Days Aged', 'Purchase Date', 'Item #', 'Vendor', 'Item Name', 'Quantity', 'Cost', 'Price','Turn over Ratio' ]);
        NewResults1.forEach(function(item){
          var itemDetail = getEarliestChkTxnForItem(that.state.Results, item.ItemId, asOfDate);
          if(!itemDetail) {
            return;
          }

          const soldQty = getOutQuantityForItem(
            that.state.Results,
            item.ItemId,
            asOfDate,
            that.toQty
          );
          const row = buildTurnOverStyleRow(item, itemDetail, asOfDate, soldQty);
          // Inventory Turn Over Report excludes 0-days-aged items.
          if (row.daysAged <= 0) {
            return;
          }

            that.state.data.push([
              row.daysAged,
              row.purchaseDate,
              row.itemNumber,
              row.vendorName,
              row.itemName,
              formatReportQty(row.quantity),
              formatReportMoney(row.cost),
              formatReportMoney(row.price),
              formatReportQty(row.turnOverRatio),
            ]);
          
        });
       }
      
}

  const excelBuffer = createCsvContent(this.state.data);
  const currentDateTime = new Date();
  // Format date-time stamp
  const formattedDateTime = `${currentDateTime.toLocaleDateString()} ${currentDateTime.toLocaleTimeString()}`;
  this.saveExcelFile(excelBuffer, 'InventoryTurnOver' + '_report_'+formattedDateTime+'.csv');

  const { history } = this.props;
history.push('/vendor-details');
  setTimeout(() => {
    history.push('/reports');
   
  }, 1); 



})
.catch(error => this.handleApiFailure(error, 'Unable to generate the inventory turnover report.'));
  
}

generatePSReportArray(transactionResults, reportEndDate) {
  const accessToken = getAccessToken();
  const that = this;
  const purchaseTransactions =
    transactionResults != null ? transactionResults : this.state.Results || [];
  const asOfDate =
    reportEndDate != null && reportEndDate !== ''
      ? reportEndDate
      : this.state.search.endDate;
  const params = {
    date: formatReportApiDate(asOfDate),
    categoryIds: this.state.selectedList.map((e) => e.CategoryId).join(','),
  };

  const resetReportSearchState = () => {
    this.setState({
      search: {
        startDate: null,
        endDate: null,
        vendorId: 0,
        customerId: 0,
        ponumber: '',
        ticketnumber: '',
        tabindex: 0,
        report: '',
      },
    });
    this.setState({ selectedList: [] });
  };

  apiClient
    .get(`/api/get/itemByDate`, { params, headers: { Authorization: `Bearer ${accessToken}` } })
    .then(async (res) => {
      const NewResults = res.data;
      if (NewResults.length === 0) {
        const errorMessage =
          'No record available for inventory for from date, Please select another date.';
        setTimeout(() => {
          this.setState({ errorMessage });
        }, 1000);
        setTimeout(() => {
          this.setState({ errorMessage: null });
        }, 6000);
        resetReportSearchState();
        return;
      }

      const itemsWithQty = (NewResults || []).filter((item) => that.toQty(item.Quantity) > 0);
      if (itemsWithQty.length === 0) {
        const errorMessage =
          'No record available for inventory for from date, Please select another date.';
        setTimeout(() => {
          this.setState({ errorMessage });
        }, 1000);
        setTimeout(() => {
          this.setState({ errorMessage: null });
        }, 6000);
        resetReportSearchState();
        return;
      }

      // Same detail columns as Inventory Turn Over, plus age color coding and summary.
      let totalQty = 0;
      let agedQty = 0;
      const headerStyle = { font: { bold: true } };
      this.state.data.push([
        { v: 'Days Aged', s: headerStyle },
        { v: 'Purchase Date', s: headerStyle },
        { v: 'Item #', s: headerStyle },
        { v: 'Vendor', s: headerStyle },
        { v: 'Item Name', s: headerStyle },
        { v: 'Quantity', s: headerStyle },
        { v: 'Cost', s: headerStyle },
        { v: 'Price', s: headerStyle },
        { v: 'Turn over Ratio', s: headerStyle },
      ]);

      itemsWithQty.forEach((item) => {
        const itemDetail = getEarliestChkTxnForItem(
          purchaseTransactions,
          item.ItemId,
          asOfDate
        );
        if (!itemDetail) {
          return;
        }

        const soldQty = getOutQuantityForItem(
          purchaseTransactions,
          item.ItemId,
          asOfDate,
          that.toQty
        );
        const row = buildTurnOverStyleRow(item, itemDetail, asOfDate, soldQty);
        // PS Report includes 0-days-aged items (unlike Turn Over Report).
        const qty = Math.max(0, row.quantity);
        totalQty += qty;
        if (isAgedInventoryUnit(row.daysAged)) {
          agedQty += qty;
        }

        that.state.data.push([
          inventoryAgeStyledCell(row.daysAged, row.daysAged),
          inventoryAgeStyledCell(row.purchaseDate, row.daysAged),
          inventoryAgeStyledCell(row.itemNumber, row.daysAged),
          inventoryAgeStyledCell(row.vendorName, row.daysAged),
          inventoryAgeStyledCell(row.itemName, row.daysAged),
          inventoryAgeQtyCell(qty, row.daysAged),
          inventoryAgeMoneyCell(row.cost, row.daysAged),
          inventoryAgeMoneyCell(row.price, row.daysAged),
          inventoryAgeQtyCell(row.turnOverRatio, row.daysAged),
        ]);
      });

      const agedPct =
        totalQty > 0 ? Number(((agedQty / totalQty) * 100).toFixed(2)) : 0;
      const summaryColor = 'E8E8E8';

      this.state.data.push([]);
      this.state.data.push([
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        inventoryAgeBoldCell('Total Qty', summaryColor),
        inventoryAgeBoldCell(totalQty, summaryColor),
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
      ]);
      this.state.data.push([
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        inventoryAgeBoldCell('Aged Units Qty (15+ days)', summaryColor),
        inventoryAgeBoldCell(agedQty, summaryColor),
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
      ]);
      this.state.data.push([
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        inventoryAgeBoldCell('% Aged Units', summaryColor),
        inventoryAgeBoldCell(`${formatReportQty(agedPct)}%`, summaryColor),
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
        { v: '', s: { fill: { fgColor: { rgb: summaryColor } } } },
      ]);

      const excelBuffer = await createXlsxBuffer(this.state.data);
      const currentDateTime = new Date();
      const formattedDateTime = `${currentDateTime.toLocaleDateString()} ${currentDateTime.toLocaleTimeString()}`;
      this.saveExcelFile(excelBuffer, 'PSReport' + '_report_' + formattedDateTime + '.xlsx');

      resetReportSearchState();

      const { history } = this.props;
      history.push('/vendor-details');
      setTimeout(() => {
        history.push('/reports');
      }, 1);
    })
    .catch(error => this.handleApiFailure(error, 'Unable to generate the PS report.'));
}

generateInventoryAgeArray(){
 let accessToken = getAccessToken();
  const asOfDate =
    this.state.search.endDate != null && this.state.search.endDate !== ''
      ? this.state.search.endDate
      : this.state.endDate;
  const params = {
    date: formatReportApiDate(asOfDate),
      categoryIds: this.state.selectedList.map(e => e.CategoryId).join(","),
    };

    
  apiClient.get(`/api/get/itemByDate`, { params: params ,headers: {"Authorization" : `Bearer ${accessToken}`} })
  .then(async res => {
    const NewResults = res.data;
    if(NewResults.length == 0)
      {

        var errorMessage = 'No record available for inventory for from date, Please select another date.';
        setTimeout(() => {
          this.setState({ errorMessage:errorMessage });
  
        }, 1000);
         
        setTimeout(() => {
          this.setState({ errorMessage: null });
         }, 6000);

         this.setState({search:{startDate:null,endDate:null,vendorId:0,customerId:0,ponumber:"",ticketnumber:"",tabindex:0,report:'',}});
        this.setState({selectedList:[]});

         return;

      }else{

        this.setState({search:{startDate:null,endDate:null,vendorId:0,customerId:0,ponumber:"",ticketnumber:"",tabindex:0,report:''}});
        this.setState({selectedList:[]});
       if(NewResults.length > 0)
       {
        var that = this;
var  NewResults1 = NewResults.filter(x=>that.toQty(x.Quantity) > 0);
       const reportDate = parseReportMoment(asOfDate);

       this.state.data.push([
        {v:'Days Aged', s: { font: { bold: true } }}
        ,  {v:'Purchase Date', s: { font: { bold: true } }}
        ,  {v:'MetrcUID', s: { font: { bold: true } }}
        ,  {v:'Vendor', s: { font: { bold: true } }}
        ,  {v:'Item Name', s: { font: { bold: true } }}
        ,  {v:'Quantity', s: { font: { bold: true } }}
        ,  {v:'Cost', s: { font: { bold: true } }}
        ,  {v:'Price' , s: { font: { bold: true } }}
      ]);
        NewResults1.forEach(function(item){
          var itemDetail = getEarliestChkTxnForItem(that.state.Results, item.ItemId, asOfDate);
          if(itemDetail)
          {
            
            const date1 = parseReportMoment(itemDetail.Date); // First date
           const date2 = reportDate.isValid() ? reportDate : moment();
           const diffInDays = date2.diff(date1, "days");
            var rowColor = "FFFFFF" ;
            // FFA500  orange
            // FFFF00  yellow
            //  FFCCCC       red

            if(diffInDays >= 15 && diffInDays <= 21)
              rowColor = "FFFF00" ; 
              else if(diffInDays >= 22 && diffInDays <= 35)
                rowColor = "FFA500" ;
             else if(diffInDays >= 36 )
              rowColor = "FF0000" ;

            that.state.data.push([
              {v:diffInDays , s: { fill: { fgColor: { rgb: rowColor } } }}
              , {v:itemDetail.Date , s: { fill: { fgColor: { rgb: rowColor } } }}
              , {v:(item.MetricUID || item.MetrcUID || item.MatrcUID || item.MatricUID || ''), s: { fill: { fgColor: { rgb: rowColor } } }}
              ,{v:item.VendorName , s: { fill: { fgColor: { rgb: rowColor } } }}
              , {v:that.formatItemNumberAndName(itemDetail.ItemNumber || item.ItemNumber, item.ItemName), s: { fill: { fgColor: { rgb: rowColor } } }}
              , reportQtyCell(item.Quantity, rowColor)
              , reportMoneyCell(itemDetail.PricePerUnit, rowColor)
              , reportMoneyCell(itemDetail.TargetPrice == null ? 0 : itemDetail.TargetPrice, rowColor)
              ]);
          }
          
        });
       }
      
}

  const excelBuffer = await createXlsxBuffer(this.state.data);
  const currentDateTime = new Date();
  // Format date-time stamp
  const formattedDateTime = `${currentDateTime.toLocaleDateString()} ${currentDateTime.toLocaleTimeString()}`;
  this.saveExcelFile(excelBuffer, 'InventoryAge' + '_report_'+formattedDateTime+'.xlsx');

  const { history } = this.props;
history.push('/vendor-details');
  setTimeout(() => {
    history.push('/reports');
   
  }, 1); 



})
.catch(error => this.handleApiFailure(error, 'Unable to generate the inventory age report.'));
  
}

generateEODArray() {
  const accessToken = getAccessToken();
  const params = {
    date: this.state.search.endDate,
    categoryIds: this.state.selectedList.map(e => e.CategoryId).join(','),
  };

  apiClient.get(`/api/get/itemByDate`, { params: params, headers: { Authorization: `Bearer ${accessToken}` } })
    .then(async res => {
      const that = this;
      const items = (res.data || []).filter(
        (item) =>
          hasNonZeroInventoryQty(item.Quantity) ||
          hasNonZeroInventoryQty(item.Reserve)
      );
      if (items.length === 0) {
        const errorMessage = 'No record available for inventory for selected date. Please select another date.';
        setTimeout(() => {
          this.setState({ errorMessage: errorMessage });
        }, 1000);
        setTimeout(() => {
          this.setState({ errorMessage: null });
        }, 6000);
        this.setState({
          search: { startDate: null, endDate: null, vendorId: 0, customerId: 0, ponumber: '', ticketnumber: '', tabindex: 0, report: '' },
        });
        this.setState({ selectedList: [] });
        this.setState({ loading: false });
        return;
      }

      const reportDate = moment(this.state.search.endDate).format('MM/DD/YYYY');
      this.state.data.push([
        eodStyledCell(`EOD Report - ${reportDate}`, 'D3D3D3', true),
        eodStyledCell('', 'D3D3D3', true),
        eodStyledCell('', 'D3D3D3', true),
        eodStyledCell('', 'D3D3D3', true),
        eodStyledCell('', 'D3D3D3', true),
        eodStyledCell('', 'D3D3D3', true),
      ]);
      this.state.data.push([
        eodStyledCell('Item Number + Item Name', 'D3D3D3', true),
        eodStyledCell('Orig. Name', 'D3D3D3', true),
        eodStyledCell('Vendor', 'D3D3D3', true),
        eodStyledCell('End Inv.', 'D3D3D3', true),
        eodStyledCell('Res.', 'D3D3D3', true),
        eodStyledCell('End Inv. w Res.', 'D3D3D3', true),
      ]);

      const sortedItems = [...items].sort((a, b) =>
        String(a.ItemNumber || '').localeCompare(String(b.ItemNumber || ''), undefined, { numeric: true })
      );

      sortedItems.forEach((item, index) => {
        const endInv = that.toQty(item.Quantity);
        const res = that.toQty(item.Reserve);
        const endInvWithRes = endInv + res;
        const rowColor = index % 2 === 0 ? 'FFFFFF' : 'D3D3D3';

        that.state.data.push([
          eodStyledCell(that.formatItemNumberAndName(item.ItemNumber, item.ItemName), rowColor),
          eodStyledCell(item.OrignalName || '', rowColor),
          eodStyledCell(item.VendorName || '', rowColor),
          eodQtyCell(endInv, rowColor),
          eodQtyCell(res, rowColor),
          eodQtyCell(endInvWithRes, rowColor),
        ]);
      });

      const excelBuffer = await createXlsxBuffer(this.state.data, { sheetName: 'EOD' });
      const currentDateTime = new Date();
      const formattedDateTime = `${currentDateTime.toLocaleDateString()} ${currentDateTime.toLocaleTimeString()}`;

      this.saveExcelFile(excelBuffer, 'EOD_report_' + formattedDateTime + '.xlsx');
      this.setState({
        search: { startDate: null, endDate: null, vendorId: 0, customerId: 0, ponumber: '', ticketnumber: '', tabindex: 0, report: '' },
      });
      this.setState({ selectedList: [] });

      const { history } = this.props;
      history.push('/vendor-details');
      setTimeout(() => {
        history.push('/reports');
      }, 1);
    })
    .catch(error => this.handleApiFailure(error, 'Unable to generate the EOD report.'));
}

generateEODReport() {
  this.setState({ data: [] });
  this.generateEODArray();
}

generateInventoryReport = (reporName) => {
  this.generateInventoryArray();  
};

generateInventoryAgeReport = (reporName) => {
  this.generateInventoryAgeArray();  
};

generateInventoryTurnOverReport = (reporName) => {
  this.generateInventoryTurnOverArray();  
};

generatePSReport = (transactionResults, reportEndDate) => {
  this.generatePSReportArray(transactionResults, reportEndDate);
};

generateSPLReport = () => {
  const accessToken = getAccessToken();
  const categoryIds = this.state.selectedList.map((item) => item.CategoryId).join(',');
  const { startDate, endDate } = this.state.search;
  const start = startDate ? moment(startDate) : null;
  const end = endDate ? moment(endDate) : null;
  if (start && end && start.isAfter(end, 'day')) {
    this.setState({ errorMessage: 'Start date must be on or before the end date.' });
    setTimeout(() => this.setState({ errorMessage: null }), 6000);
    return;
  }
  const hasRange = Boolean(start || end);
  const periodLabel = hasRange
    ? `${start ? start.format('MM/DD/YYYY') : 'Beginning'} - ${(end || moment()).format('MM/DD/YYYY')}`
    : '';
  const salesLabel = hasRange ? '' : ' YTD';

  this.setState({ loading: true, errorMessage: '', data: [] });
  apiClient
    .get('/api/get/spl-report', {
      params: {
        categoryIds,
        startDate: start ? start.format('YYYY-MM-DD') : undefined,
        endDate: end ? end.format('YYYY-MM-DD') : undefined,
        hideInactive: this.state.splHideInactive ? 1 : undefined,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    .then(async (res) => {
      const rows = Array.isArray(res.data) ? res.data : [];
      if (rows.length === 0) {
        this.setState({
          loading: false,
          errorMessage: this.state.splHideInactive
            ? 'No items with activity for the selected category and period.'
            : 'No items are available for the selected category.',
        });
        setTimeout(() => this.setState({ errorMessage: null }), 6000);
        return;
      }

      const headerStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '1A237E' } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      };
      const titleStyle = {
        font: { bold: true, sz: 16, color: { rgb: '1A237E' } },
        alignment: { horizontal: 'center', vertical: 'center' },
      };
      const headers = [
        'Item Number + Item Name',
        'Total Qty Purchased',
        'Total Qty Sold',
        'Available Qty',
        'Lights',
        'Target Price',
        `Total Sales ($)${salesLabel}`,
        `Sales / Light${salesLabel}`,
        'Projected Sales / Light',
      ];
      const sheetData = [
        [{ v: hasRange ? `Sales Per Light (${periodLabel}, by transaction date)` : 'Sales Per Light', s: titleStyle }],
        headers.map((value) => ({ v: value, s: headerStyle })),
      ];

      rows.forEach((row) => {
        const hasLights = Number(row.Lights) > 0;
        sheetData.push([
          this.formatItemNumberAndName(row.ItemNumber, row.ItemName),
          reportQtyCell(row.TotalQtyPurchased, 'FFFFFF'),
          reportQtyCell(row.TotalQtySold, 'FFFFFF'),
          reportQtyCell(row.AvailableQty, 'FFFFFF'),
          reportQtyCell(row.Lights, 'FFFFFF'),
          reportMoneyCell(row.TargetPrice, 'FFFFFF'),
          reportMoneyCell(row.TotalSalesYtd, 'FFFFFF'),
          hasLights ? reportMoneyCell(row.SalesPerLightYtd, 'FFFFFF') : '-',
          hasLights ? reportMoneyCell(row.ProjectedSalesPerLight, 'FFFFFF') : '-',
        ]);
      });

      const excelBuffer = await createXlsxBuffer(sheetData, {
        sheetName: 'Sales Per Light',
        merges: ['A1:I1'],
        columnWidths: [34, 20, 16, 16, 12, 16, 20, 20, 25],
        rowHeights: [26, 34],
      });
      this.saveExcelFile(
        excelBuffer,
        hasRange
          ? `SPLReport_${start ? start.format('YYYY-MM-DD') : 'start'}_to_${(end || moment()).format('YYYY-MM-DD')}.xlsx`
          : `SPLReport_${moment().format('YYYY-MM-DD')}.xlsx`
      );
      this.setState({
        loading: false,
        selectedList: [],
        search: {
          startDate: null,
          endDate: null,
          vendorId: 0,
          customerId: 0,
          ponumber: '',
          ticketnumber: '',
          tabindex: 0,
          report: '',
        },
      });
    })
    .catch((error) => {
      const responseMessage =
        error && error.response && error.response.data && error.response.data.error;
      this.setState({
        loading: false,
        errorMessage: responseMessage || 'Unable to generate the SPL Report.',
      });
      this.props.showFlashNotification(
        responseMessage || 'Unable to generate the SPL Report.',
        'error'
      );
      setTimeout(() => this.setState({ errorMessage: null }), 6000);
    });
};


getTransactionType = (TicketNumber) =>{

  if(TicketNumber != undefined)
  {
    if(TicketNumber.includes('OUT'))
    {
      return 'Invoice';
    }
    else if(TicketNumber.includes('CHK'))
    {
      return  'PO';
    }
    else if(TicketNumber.includes('RET'))
    {
      return  'PO';
    }
    else if(TicketNumber.includes('RES'))
    {
      return 'Invoice';
    }
    else if(TicketNumber.includes('ADJ'))
    {
      return 'Invoice';
    }
  }else{
    return '';
  }
   
  
  };


setShowTranDetail = (date) => {
   this.setState({ showTranDetail: false });
  
};

handleChildChange = () => {
  this.setShowTranEdit(false);
  const { history } = this.props;
  
  this.state.search["startDate"] = null;
  this.state.search["endDate"] = null;
  this.state.search["vendorId"] = 0;
  this.state.search["customerId"] = 0;
  this.state.search["ponumber"] = "";
  this.state.search["ticketnumber"] = "";
  this.state.search["tabindex"] = this.state.index;
  
  this.setState({
    search:  this.state.search
   });
  history.push('/vendor-details');
  setTimeout(() => {
   history.push('/transactions');
 }, 1);

  };

setShowTranEdit = (date,action) => {
  this.setState({ action: action });
  this.setState({ showTranEdit: false });
 
};


   

    handleSetSearchDate = (value,type) => {
      

      if(type=='start')
      {
        this.state.search["startDate"] = value;
        this.state.search["tabindex"] = this.state.index;
        this.setState({
          search: this.state.search,
         });

         
      }else if(type=='end'){
        this.state.search["endDate"] = value;
        this.state.search["tabindex"] = this.state.index;
        this.setState({
          search: this.state.search,
         });
      }else{
        this.state.search["report"] = value.currentTarget.dataset.value;
        this.state.search["tabindex"] = this.state.index;
        this.setState({
          search: this.state.search,
         });
      }
    };
   
    handleSetSearchData = (event) => {
      this.state.search[event.target.name] = event.target.value;
      this.state.search["tabindex"] = this.state.index;
      this.setState({
        search: this.state.search,
       });
    };

    componentDidMount() {
      let user = JSON.parse(sessionStorage.getItem('user'));
      let accessToken = getAccessToken();
      sessionStorage.setItem('TranSearch', null);

      apiClient.get(`/api/get/category`, { headers: {"Authorization" : `Bearer ${accessToken}`} })
                .then(res => {
                  const categoryList = res.data;
                  this.setState({ categoryList });
                })
                .catch(error => this.handleApiFailure(error, 'Unable to load report categories.'));

       if(user.permissions.filter(x=>x.PermissionName == 'Reports' && x.PermissionAllowed == 1).length == 0)
         {
            const { history } = this.props;
            history.push('/home');
         }
     
 

     
    };
    a11yProps(index) {
      return {
        id: `scrollable-auto-tab-${index}`,
        'aria-controls': `scrollable-auto-tabpanel-${index}`,
      };
    }
      

    onTabClicked = (event, newValue) => {
      const { history } = this.props;
      this.setState({ index: newValue });
      this.state.search["startDate"] = null;
      this.state.search["endDate"] = null;
      this.state.search["vendorId"] = 0;
      this.state.search["customerId"] = 0;
      this.state.search["ponumber"] = "";
      this.state.search["ticketnumber"] = "";
      this.state.search["tabindex"] = newValue;
      
      this.setState({
        search:  this.state.search
       });
      history.push('/vendor-details');
         setTimeout(() => {
          history.push('/transactions');
        }, 1);
      
     
  }
    
    handleMoreSearch = () => {

      const { history } = this.props;
      history.push('/vendor-details');
         setTimeout(() => {
          history.push('/transactions');
        }, 1); 
      
      


    };


    generateReport = (search) => {
      const isNoDateReport = OPTIONAL_DATE_REPORTS.includes(this.state.search.report);
      const hasRequiredDates = isNoDateReport
        ? true
        : SINGLE_DATE_REPORTS.includes(this.state.search.report)
        ? (this.state.search.endDate != '' && this.state.search.endDate != null && this.state.search.endDate != undefined)
        : (this.state.search.startDate != '' && this.state.search.startDate != null && this.state.search.startDate != undefined
          && this.state.search.endDate != '' && this.state.search.endDate != null && this.state.search.endDate != undefined);

	if (
      this.state.search.report === 'SPLReport' &&
      this.state.selectedList.length === 0
    ) {
      this.setState({ errorMessage: 'Please select at least one item category.' });
      setTimeout(() => this.setState({ errorMessage: null }), 6000);
      return;
    }

 if( this.state.search.report  != '' && hasRequiredDates)
		{

      if (this.state.search.report === 'SPLReport') {
        this.generateSPLReport();
        return;
      }

      if (this.state.search.report === 'EODReport') {
        this.setState({ data: [], loading: true });
        this.generateEODReport();
        setTimeout(() => {
          this.setState({ loading: false });
        }, 2000);
        return;
      }

      if(this.state.search.report == 'InventoryReport' && moment(this.state.search.endDate).diff(moment(this.state.search.startDate), 'days') > 7)
      {
        var errorMessage = 'Difference between report dates should not be more than 7.';
        setTimeout(() => {
          this.setState({ errorMessage:errorMessage });
  
        }, 1000);
         
        setTimeout(() => {
          this.setState({ errorMessage: null });
         }, 3000);

         return;

      }


        this.setState({Results:[]});
        this.setState({data:[]});
      
        this.setState({ loading: true });
        let accessToken = getAccessToken();
       
        const params = {
          ponumber:  this.state.search.ponumber ,
          ticketnumber:  this.state.search.ticketnumber ,
          startDate:  SINGLE_DATE_REPORTS.includes(this.state.search.report)? '': moment(this.state.search.startDate).format('MM-DD-YYYY'),
          endDate:  moment(this.state.search.endDate).format('MM-DD-YYYY') ,
          vendorId:  this.state.search.vendorId ,
          customerId:  this.state.search.customerId ,
          categoryIds: this.state.selectedList.map(e => e.CategoryId).join(","),
          transactionType: TURNOVER_TRANSACTION_REPORTS.includes(this.state.search.report)
            ? 'TurnOver'
            : CHK_TRANSACTION_REPORTS.includes(this.state.search.report)
              ? 'CHK'
              : '',
          type:'report'
          };
          if (
            this.state.search.report === 'OrderReport' ||
            this.state.search.report === 'InventoryReport'
          ) {
            params.excludeArchived = 1;
            params.includeInactive = 1;
            params.inventoryItemsOnly = 1;
          }
          this.setState({
            startDate: this.state.search.startDate,
            endDate: this.state.search.endDate,
          });
         
         
        apiClient.get(`/api/get/transaction`,{params:params, headers: {"Authorization" : `Bearer ${accessToken}`} })
        .then(res => {
          const Results = res.data;
          this.setState({fullGroupData: Results});
          this.setState({Results});
          if(this.state.search.report == 'OrderReport')
          {
            this.generateOrderReport('Order');
            this.setState({search:{startDate:null,endDate:null,vendorId:0,customerId:0,ponumber:"",ticketnumber:"",tabindex:0,report:''}});
            this.setState({selectedList:[]});
 
          }
          else if(this.state.search.report == 'InventoryReport')
            {
            var  params = {
          ponumber:  '' ,
          ticketnumber:  '' ,
          startDate:  '',
          endDate:  '' ,
          vendorId:  0 ,
          customerId:  0,
          categoryIds: this.state.selectedList.map(e => e.CategoryId).join(","),
          transactionType:'CHK',
          type:'report'
          };
              apiClient.get(`/api/get/transaction`,{params:params, headers: {"Authorization" : `Bearer ${accessToken}`} })
              .then(res => {
                const ItemsPurchaseInfo = res.data;
                this.setState({ItemsPurchaseInfo:ItemsPurchaseInfo});
                  this.generateInventoryReport('Inventory');
                  this.setState({search:{startDate:null,endDate:null,vendorId:0,customerId:0,ponumber:"",ticketnumber:"",tabindex:0,report:''}});
                  this.setState({selectedList:[]});
              })
              .catch(error => this.handleApiFailure(
                error,
                'Unable to load inventory purchase transactions.'
              ));


          
 
          }
           else if(this.state.search.report == 'InventoryAgeReport')
            {
            this.generateInventoryAgeReport();
            this.setState({search:{startDate:null,endDate:null,vendorId:0,customerId:0,ponumber:"",ticketnumber:"",tabindex:0,report:''}});
            this.setState({selectedList:[]});
 
          }
          else if(this.state.search.report == 'InventoryTurnoverReport')
            {
            this.generateInventoryTurnOverReport();
            this.setState({search:{startDate:null,endDate:null,vendorId:0,customerId:0,ponumber:"",ticketnumber:"",tabindex:0,report:''}});
            this.setState({selectedList:[]});
 
          }
          else if(this.state.search.report == 'PSReport')
            {
            const reportEndDate = this.state.search.endDate;
            this.generatePSReport(Results, reportEndDate);
            this.setState({search:{startDate:null,endDate:null,vendorId:0,customerId:0,ponumber:"",ticketnumber:"",tabindex:0,report:''}});
            this.setState({selectedList:[]});
 
          }
         
          setTimeout(() => {
            this.setState({ loading: false });
           }, 2000);
        })
        .catch(error => this.handleApiFailure(error, 'Unable to load report transactions.'));
     

     
    }else{
      var errorMessage = 'Please select these things ';

      if( this.state.search.report  == '' || this.state.search.report  == undefined)
      errorMessage += ' report,'

      if (!isNoDateReport && !SINGLE_DATE_REPORTS.includes(this.state.search.report)
        && (this.state.search.startDate == '' || this.state.search.startDate == undefined)) {
        errorMessage += ' start Date,';
      }

      if (!isNoDateReport && (this.state.search.endDate == '' || this.state.search.endDate == undefined)) {
        errorMessage += SINGLE_DATE_REPORTS.includes(this.state.search.report) ? ' date' : ' end Date';
      }
			
      setTimeout(() => {
        this.setState({ errorMessage:errorMessage });

      }, 1000);
       
      setTimeout(() => {
        this.setState({ errorMessage: null });
       }, 10000);
    }
  };

    generatePdf = () => {
      const input = document.getElementById('alert-dialog-description');
  
      html2canvas(input)
        .then((canvas) => {
          const pdf = new jsPDF('p', 'px', 'a4');
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();
          const scaleFactor = Math.min(pdfWidth / canvas.width, pdfHeight / canvas.height);
          const scaledWidth = canvas.width * scaleFactor;
          const scaledHeight = canvas.height * scaleFactor;
          pdf.addImage(canvas, 'PNG', 0, 0, scaledWidth, scaledHeight);
          pdf.save('generated.pdf');
        });
    };
    saveExcelFile = (buffer, fileName) => {
      const data = buffer instanceof Blob
        ? buffer
        : new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
      saveAs(data, fileName);
    };
    

    
      
    applyQuery = (query, data) => {
      let filteredData = [...data];
    
      // Apply search filter
      if (query.search) {
        filteredData = filteredData.filter(
          row =>
            row.name.toLowerCase().includes(query.search.toLowerCase()) ||
            row.age.toString().includes(query.search)
        );
      }
    
      // Apply sorting
      if (query.orderBy) {
        const field = query.orderBy.field;
        const direction = query.orderDirection === 'desc' ? -1 : 1;
        filteredData.sort((a, b) => {
          if (a[field] < b[field]) return -direction;
          if (a[field] > b[field]) return direction;
          return 0;
        });
      }
    
      // Apply paging
      if (query.pageSize) {
        filteredData = filteredData.slice(
          query.page * query.pageSize,
          query.page * query.pageSize + query.pageSize
        );
      }
    
      return filteredData;
    };

    renderTable = (data) => {
      const  user = JSON.parse(sessionStorage.getItem('user'));
      
      ReactDOM.render(
        <MaterialTable
          columns={ this.state.index == 2? ( user.permissions.filter(x=>x.PermissionName == 'Edit Transaction' && x.PermissionAllowed == 1).length > 0 ? this.state.columnsTicket: this.state.columnsTicketNonEditable):( user.permissions.filter(x=>x.PermissionName == 'Edit Transaction' && x.PermissionAllowed == 1).length > 0 ? this.state.columnsTicketReturn: this.state.columnsTicketNonEditableReturn)}
          title={ this.state.index == 0?'Invoices List':this.state.index == 1?'Purchase Orders List':this.state.index == 2?'Returns List':this.state.index == 3?'Reserves List':'Adjustments List'}
          data={data}
          options={{
            sorting: true,
            search: true,
            paging: true,
          }}
         
        />,
        document.getElementById('data-table-container')
      );
    };
    
  render() {
    return (
      
      <div style={{ maxWidth: '100%' }} className="Report">
       <h2>Reports</h2>
         <form   style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>

           <div  >
          <FormControl  margin="normal">
						<InputLabel id="select-label">Select Report</InputLabel>
						<Select
										label="Report"
										name="report" 
										value={this.state.search.report}
                  onChange={(e) => this.handleSetSearchDate(e,'report')}
										margin="normal"
										displayEmpty
										inputProps={{ 'aria-label': 'Without label' }} >
									<MenuItem value="OrderReport">  Sale and Order Report</MenuItem>
									<MenuItem value="InventoryReport">  Inventory Transaction Report</MenuItem>
                  <MenuItem value="EODReport">  EOD (End of Day) Report</MenuItem>
                  <MenuItem value="InventoryAgeReport">  Inventory Age Report</MenuItem>
                  <MenuItem value="InventoryTurnoverReport">  Inventory Turn Over Report</MenuItem>
                  <MenuItem value="PSReport">  PS Report</MenuItem>
                  <MenuItem value="SPLReport">  SPL Report</MenuItem>
						 </Select>  
           </FormControl>
                
          </div>

           {
            !SINGLE_DATE_REPORTS.includes(this.state.search.report) &&
            (
    <div >
                      <FormControl  margin="normal">
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <DatePicker
                          label= "Start Date"
                          name="startDate"
                          format="MM/dd/yyyy"
                          value={this.state.search.startDate}
                          onChange={(e) => this.handleSetSearchDate(e,'start')}
                          displayEmpty
                          margin="normal"
                          clearable
                        />
                        </MuiPickersUtilsProvider>
                      </FormControl>
              </div>
            )
          }
         
          {<div>
                  <FormControl  margin="normal">
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DatePicker
                      name="endDate"
                      label={(SINGLE_DATE_REPORTS.includes(this.state.search.report) ? "Select Date" : "End Date")}
                      format="MM/dd/yyyy"
                      value={this.state.search.endDate}
                      onChange={(e) => this.handleSetSearchDate(e,'end')}
                      displayEmpty
                      margin="normal"
                      clearable
                    />
                    </MuiPickersUtilsProvider>
                  </FormControl>
          </div>}
         
      
         <div style={{ width: "250px" , paddingTop:"40px"}}>
           <Multiselect
            styles={{
          control: (provided) => ({
            ...provided,
            minWidth: "200px",   // set width
            maxWidth: "300px"    // optional
          })
        }}
      options={this.state.categoryList}
      displayValue="CategoryName"
      placeholder="Select Item Category"
      onSelect={(selectedList) => this.setState({selectedList:selectedList})}
      onRemove={(selectedList) => this.setState({selectedList:selectedList})}
    />
         </div>
        
                <br/>
                <br/>

         </form>

         {this.state.search.report === 'SPLReport' && (
           <div>
             <h3 className="spl-report-title">Sales Per Light</h3>
             <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)', marginBottom: 4 }}>
               Dates filter by transaction (ticket) date. Without dates: quantities are all-time, sales are year-to-date.
             </div>
             <FormControlLabel
               control={
                 <Checkbox
                   color="primary"
                   checked={this.state.splHideInactive}
                   onChange={(e) => this.setState({ splHideInactive: e.target.checked })}
                 />
               }
               label="Hide items without activity (0 purchased, 0 sold, 0 available)"
             />
           </div>
         )}


         <br></br> 
       <br></br> 
       <br></br> 
          <div style={{textAlign:'left', paddingBottom: '3px',marginTop:'5px'}}>
          
            
       <Button
                    color="primary"
                    style= {{ width: '250px' , textAlign:'left'}}
                    variant="contained"
                    onClick={() => this.generateReport('InventoryReport')}
                    disabled={this.submitted}
                >
                 
            Generate Report
         
                </Button>

     
            </div>
        
        
        <br/>
        <div style={{textAlign:'right', paddingBottom: '3px'}}>
          
        </div>

      
   

{this.state.loading ? <Spinner /> :""}

{this.state.errorMessage && (
        <div style={{ backgroundColor: '#FF7F7F', color: '#fff', padding: '10px', marginTop: '10px' }}>
          {this.state.errorMessage}
        </div>
      )}
        
      </div>
      
    )
  }
}
export default withFlashNotification(Reports);
